import React, { useState, useRef, useEffect } from 'react';
import TimerAdjuster from './TimerAdjuster';
import SegmentedControl from './SegmentedControl';
import EndSessionModal from './EndSessionModal';
import supabase from '../supabase/supabase';

const IDLE_PRESETS     = [30, 45, 60, 120, 'Custom'];
const PRESET_MINUTES   = [30, 45, 60, 120];
const LIVE_CHIPS       = ['+1 Min', '+5 Min', '+10 Min', '+ Custom'];
const PRE_ROLL_OPTIONS = ['Off', '3s', '5s', '10s', '30s', '60s'];
const TIMER_MODE_OPTIONS = ['Count Down', 'Count Up'];
const START_MODE_OPTIONS = ['Manual', 'Auto-Start'];

/* ─────────────────────────────────────────
   Fractional color logic (Mirrors DisplayKiosk)
───────────────────────────────────────── */
function getFractionalState(timeRemaining, totalDuration, timerMode = 'down') {
  if (!totalDuration) return { color: '#ffffff', pulse: false, key: 'default' };

  const timeToTarget = timerMode === 'up'
    ? Math.max(0, totalDuration - timeRemaining)
    : timeRemaining;
  const half    = Math.round(totalDuration / 2);
  const quarter = Math.round(totalDuration / 4);

  if (timeToTarget <= 300) {
    return { color: '#D4AF37', pulse: false, key: 'warning' };
  }
  if (timeToTarget <= quarter && timeToTarget > quarter - 4) {
    return { color: '#D4AF37', pulse: true,  key: `pulse-quarter` };
  }
  if (timeToTarget <= half && timeToTarget > half - 4) {
    return { color: '#D4AF37', pulse: true,  key: `pulse-half` };
  }
  return { color: '#ffffff', pulse: false, key: 'default' };
}

export default function MainStage({ activeRoom }) {
  // DB State
  const roomId = activeRoom?.id;
  const roomName = activeRoom?.name || '';
  const [sessionData, setSessionData] = useState(null);

  // Local UI State
  const [isPending, setIsPending]           = useState(false);
  const [localTime, setLocalTime]           = useState(3600);
  const [localPreRoll, setLocalPreRoll]     = useState(0);
  const [localPhase, setLocalPhase]         = useState(null); // tracks 'preroll' | 'active' | 'complete' | 'idle'
  const [activePreset, setActivePreset]     = useState(60);
  const [customMin, setCustomMin]           = useState('');
  const [customAdd, setCustomAdd]           = useState('');
  const [preRoll, setPreRoll]               = useState('Off');
  const [timerMode, setTimerMode]           = useState('down');
  const [startMode, setStartMode]           = useState('Manual');
  const [schedTime, setSchedTime]           = useState('10:00');
  const [liveNote, setLiveNote]             = useState('');
  const [activeLiveChip, setActiveLiveChip] = useState('+5 Min');
  const [copyFeedback, setCopyFeedback]     = useState(false);
  const [showEndModal, setShowEndModal]     = useState(false);

  // Share Room State
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareEmail, setShareEmail]             = useState('');
  const [shareFeedback, setShareFeedback]       = useState('');
  const [isSharing, setIsSharing]               = useState(false);

  const customInputRef = useRef(null);
  const customAddRef   = useRef(null);
  const cueTimeoutRef  = useRef(null);
  const serverTimeOffset = useRef(0); // Hardware clock drift offset
  const requestRef       = useRef(null); // Animation frame reference for aggressive unmounting
  const timerModeInitialized = useRef(false);
  const firingRef        = useRef(false); // Prevents duplicate auto-start fires

  const dbStatus   = sessionData?.status || 'idle';
  const status     = localPhase || dbStatus; // Mathematically override status if active
  const isIdle     = status === 'idle';
  const isPreRoll  = status === 'preroll';
  const isRunning  = status === 'active';
  const isPaused   = status === 'paused';
  const isComplete = status === 'complete';
  const isLive     = isPreRoll || isRunning || isPaused;

  const scheduledStart = sessionData?.scheduled_start || null;
  const isArmed        = !!scheduledStart && dbStatus === 'idle';

  const idleDuration = activePreset !== 'Custom' 
    ? activePreset * 60 
    : (parseInt(customMin, 10) || 60) * 60;

  /* ── Smooth Clock UI Fades (Robust Self-Healing Fader) ── */
  const [clockMode, setClockMode]       = useState('idle');
  const [clockVisible, setClockVisible] = useState(true);

  useEffect(() => {
    if (status !== clockMode) {
      setClockVisible(false);
      const t = setTimeout(() => {
        setClockMode(status);
      }, 300); // Crisp 300ms fade for Admin UI
      return () => clearTimeout(t);
    } else if (!clockVisible) {
      // Self-healing: if modes match but opacity is 0 (due to rapid race condition), force it visible
      const t = setTimeout(() => {
        setClockVisible(true);
      }, 50);
      return () => clearTimeout(t);
    }
  }, [status, clockMode, clockVisible]);

  const showCompleteClock = clockMode === 'complete';
  const showPreRollClock  = clockMode === 'preroll';
  const showPausedClock   = clockMode === 'paused';

  /* ── Database Initialization & Realtime Sync ── */
  useEffect(() => {
    if (!roomId) return;
    let channel;

    const fetchSession = async () => {
      let { data: session, error } = await supabase.from('studio_sessions').select('*').eq('room_id', roomId).single();
      if (error && error.code !== 'PGRST116') console.error('Error fetching session data:', error);
      setSessionData(session || null);
    };

    const initSession = async () => {
      // Sync clock drift first using atomic DB time
      try {
        const { data: serverTime, error: timeErr } = await supabase.rpc('get_server_time');
        if (!timeErr && serverTime) {
          serverTimeOffset.current = new Date(serverTime).getTime() - Date.now();
        }
      } catch (err) {
        console.warn('Clock sync failed:', err);
      }

      await fetchSession();

      channel = supabase.channel(`public:studio_sessions:${roomId}`)
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'studio_sessions',
          filter: `room_id=eq.${roomId}` // Added network layer filter to prevent global data leak
        }, (payload) => {
          if (payload.new && payload.new.room_id === roomId) {
            setSessionData(payload.new);
          }
        })
        .subscribe();
    };

    initSession();

    // Network watchdog for Wi-Fi dropout recovery
    const handleOnline = () => fetchSession();
    window.addEventListener('online', handleOnline);

    return () => { 
      if (channel) supabase.removeChannel(channel); 
      window.removeEventListener('online', handleOnline);
    };
  }, [roomId]);

  useEffect(() => {
    if (sessionData && !timerModeInitialized.current) {
      setTimerMode(sessionData.timer_mode === 'up' ? 'up' : 'down');
      timerModeInitialized.current = true;
    }
  }, [sessionData]);

  /* ── Auto-Start Scheduler ── */
  useEffect(() => {
    if (!roomId || !sessionData?.scheduled_start || sessionData.status !== 'idle') return;

    const schedMs  = new Date(sessionData.scheduled_start).getTime();
    const prSec    = sessionData.pre_roll_duration_seconds || 0;
    const dur      = sessionData.total_duration_seconds || 3600;
    const mode     = sessionData.timer_mode === 'up' ? 'up' : 'down';
    const buffer   = prSec > 0 ? 1000 : 0;
    const fireAtMs = schedMs - prSec * 1000 - buffer;

    const check = async () => {
      if (firingRef.current) return;
      const now = Date.now() + serverTimeOffset.current;
      if (now < fireAtMs) return;

      firingRef.current = true;
      try {
        // Stale schedule (missed entirely): discard instead of starting a finished session
        if (mode === 'down' && now > schedMs + dur * 1000) {
          const { data } = await supabase.from('studio_sessions')
            .update({ scheduled_start: null }).eq('room_id', roomId).select().single();
          if (data) setSessionData(data);
          return;
        }

        const timerTarget = mode === 'up'
          ? new Date(schedMs).toISOString()
          : new Date(schedMs + dur * 1000).toISOString();

        const { data, error } = await supabase.from('studio_sessions').upsert({
          room_id: roomId,
          status: 'active',
          timer_mode: mode,
          total_duration_seconds: dur,
          pre_roll_duration_seconds: prSec,
          timer_target: timerTarget,
          scheduled_start: null,
          live_cue: null
        }).select().single();

        if (error) console.error('Auto-start failed:', error);
        else setSessionData(data);
      } finally {
        firingRef.current = false;
      }
    };

    check();
    const interval = setInterval(check, 250);
    return () => clearInterval(interval);
  }, [roomId, sessionData]);

  /* ── Absolute Source of Truth Computation Engine ── */
  const transitionLock = useRef(null);
  const lastStatus = useRef(null);

  useEffect(() => {
    if (!sessionData) return;
    
    // Clear lock and local override when database status changes naturally
    if (sessionData.status !== lastStatus.current) {
      transitionLock.current = null;
      lastStatus.current = sessionData.status;
      setLocalPhase(null);
    }

    const tick = () => {
      // Guard against stale closures executing during a rapid state transition
      if (sessionData.status !== lastStatus.current) {
        requestRef.current = requestAnimationFrame(tick);
        return;
      }

      // Guard clause: If paused, skip all delta/timestamp math
      if (sessionData.status === 'paused') {
        setLocalTime(sessionData.paused_time_remaining || 0);
        requestRef.current = requestAnimationFrame(tick);
        return;
      }

      // Calculate absolute time accounting for hardware drift
      const now = Date.now() + serverTimeOffset.current;

      if (sessionData.status === 'active' && sessionData.timer_target) {
        const targetMs = new Date(sessionData.timer_target).getTime();
        const totalDurationMs = sessionData.total_duration_seconds * 1000;
        const startTimeMs = targetMs - totalDurationMs;
        const timerStartMs = sessionData.timer_mode === 'up' ? targetMs : startTimeMs;

        if (sessionData.pre_roll_duration_seconds > 0 && now < timerStartMs) {
          // Pre-Roll Phase: Time has not yet reached the mathematically derived Start Time
          const preRollRem = Math.max(0, Math.ceil((timerStartMs - now) / 1000));
          // Clamping ensures the extra 1000ms UI buffer mathematically absorbs into the first number
          const clampedPreRoll = Math.min(preRollRem, sessionData.pre_roll_duration_seconds);
          setLocalPreRoll(clampedPreRoll);
          setLocalTime(sessionData.total_duration_seconds); // Instantly prime the underlying timer to prevent visual flash during crossfade
          if (sessionData.timer_mode === 'up') setLocalTime(0);
          
          if (transitionLock.current !== 'preroll') {
            transitionLock.current = 'preroll';
            setLocalPhase('preroll'); // Automatically switch UI to pre-roll
          }
        } 
        else if (sessionData.timer_mode === 'up') {
          const elapsed = Math.max(0, Math.floor((now - targetMs) / 1000));
          setLocalTime(elapsed);
          
          if (transitionLock.current !== 'active') {
            transitionLock.current = 'active';
            setLocalPhase('active'); // Automatically switch UI to active
          }
        }
        else if (now < targetMs) {
          // Active Phase: We have crossed the start threshold (or pre-roll is disabled)
          const rem = Math.max(0, Math.ceil((targetMs - now) / 1000));
          const clampedRem = Math.min(rem, sessionData.total_duration_seconds || rem);
          setLocalTime(clampedRem);
          
          if (transitionLock.current !== 'active') {
            transitionLock.current = 'active';
            setLocalPhase('active'); // Automatically switch UI to active
          }
        } 
        else {
          // Complete Phase: We have crossed the target time
          setLocalTime(0);
          if (transitionLock.current !== 'complete') {
            transitionLock.current = 'complete';
            setLocalPhase('complete'); // Instantly lock UI to complete
            supabase.from('studio_sessions').update({
              status: 'complete',
              timer_target: null
            }).eq('room_id', roomId).select().single().then(({ data, error }) => {
              // Guard: Do not overwrite if the user already aggressively reset the room to idle manually
              if (!error && data && lastStatus.current !== 'idle') {
                setSessionData(data);
              }
            });
          }
        }
      } 
      else if (sessionData.status === 'complete') {
        setLocalTime(0);
      } 
      else if (sessionData.status === 'idle') {
        setLocalTime(idleDuration); // Lock strictly to preset selection, ignoring previous DB session length
      }

      requestRef.current = requestAnimationFrame(tick);
    };

    requestRef.current = requestAnimationFrame(tick);

    // Visibility watchdog to instantly snap clock to valid state upon waking up from background
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (sessionData.status === 'paused') {
          setLocalTime(sessionData.paused_time_remaining || 0);
          return;
        }
        // Cancel the current frame before explicitly calling tick again to prevent duplicate execution loops
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
        tick();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sessionData, roomId, idleDuration]);

  /* ── Live Cues Sync ── */
  const handleSendCue = async () => {
    if (!liveNote.trim() || !roomId) return;
    
    const { data, error } = await supabase.from('studio_sessions').update({ 
      live_cue: liveNote 
    }).eq('room_id', roomId).select().single();
    
    if (error) alert('DATABASE ERROR: ' + error.message);
    else {
      setSessionData(data);
      setLiveNote(''); // Clear input box immediately after successful send
    }
    
    if (cueTimeoutRef.current) clearTimeout(cueTimeoutRef.current);
    cueTimeoutRef.current = setTimeout(async () => {
      const { data: clearedData, error: err } = await supabase.from('studio_sessions').update({ 
        live_cue: null 
      }).eq('room_id', roomId).select().single();
      if (!err && clearedData) setSessionData(clearedData);
    }, 15000);
  };

  const handleClearCue = async () => {
    setLiveNote('');
    if (cueTimeoutRef.current) clearTimeout(cueTimeoutRef.current);
    if (roomId) {
      const { data, error } = await supabase.from('studio_sessions').update({ 
        live_cue: null 
      }).eq('room_id', roomId).select().single();
      if (!error && data) setSessionData(data);
    }
  };

  /* ── Presets & Adjustments ── */
  const handlePresetClick = (preset) => {
    setActivePreset(preset);
    if (preset === 'Custom') setTimeout(() => customInputRef.current?.focus(), 50);
    else setCustomMin('');
  };

  const adjustTime = async (amount) => {
    if (isPending) return;
    if (isIdle) {
      const nextMin = Math.max(0, Math.floor((idleDuration + amount * 60) / 60));
      const matched = PRESET_MINUTES.includes(nextMin) ? nextMin : 'Custom';
      setActivePreset(matched);
      if (matched === 'Custom') setCustomMin(String(nextMin));
    } else if (roomId) {
      setIsPending(true);
      try {
        const addSecs = amount * 60;
        
        if ((isRunning || isPreRoll) && sessionData.timer_target) {
          // Unifying Running and Pre-Roll adjustments: both must shift the target 
          // to maintain the exact absolute mathematical countdown.
          const target = new Date(sessionData.timer_target).getTime();
          if (sessionData.timer_mode === 'up') {
            const newTarget = new Date(target - addSecs * 1000).toISOString();
            const { data, error } = await supabase.from('studio_sessions').update({
              timer_target: newTarget
            }).eq('room_id', roomId).select().single();
            
            if (error) alert('DATABASE ERROR: ' + error.message);
            else setSessionData(data);
          } else {
            const newTarget = new Date(target + addSecs * 1000).toISOString();
            const { data, error } = await supabase.from('studio_sessions').update({
              timer_target: newTarget,
              total_duration_seconds: Math.max(0, sessionData.total_duration_seconds + addSecs)
            }).eq('room_id', roomId).select().single();
            
            if (error) alert('DATABASE ERROR: ' + error.message);
            else setSessionData(data);
          }
          
        } else if (isPaused) {
          if (sessionData.timer_mode === 'up') {
            const { data, error } = await supabase.from('studio_sessions').update({
              paused_time_remaining: Math.max(0, sessionData.paused_time_remaining + addSecs)
            }).eq('room_id', roomId).select().single();
            
            if (error) alert('DATABASE ERROR: ' + error.message);
            else setSessionData(data);
          } else {
            const { data, error } = await supabase.from('studio_sessions').update({
              paused_time_remaining: Math.max(0, sessionData.paused_time_remaining + addSecs),
              total_duration_seconds: Math.max(0, sessionData.total_duration_seconds + addSecs)
            }).eq('room_id', roomId).select().single();
            
            if (error) alert('DATABASE ERROR: ' + error.message);
            else setSessionData(data);
          }
        }
      } finally {
        setIsPending(false);
      }
    }
  };

  const handleCustomAddSubmit = async () => {
    if (isPending) return;
    
    const parsedVal = parseInt(customAdd, 10);
    if (isNaN(parsedVal) || parsedVal <= 0 || parsedVal > 1440) {
      setCustomAdd('');
      alert('INVALID INPUT: Please enter a valid number of minutes (1-1440).');
      return;
    }
    
    await adjustTime(parsedVal);
    setCustomAdd('');
    setActiveLiveChip('+5 Min');
  };

  /* ── Auto-Start Arm / Cancel ── */
  const validateCustomDuration = () => {
    if (activePreset === 'Custom') {
      const parsedMin = parseInt(customMin, 10);
      if (isNaN(parsedMin) || parsedMin <= 0 || parsedMin > 1440) {
        setCustomMin('');
        alert('INVALID INPUT: Please enter a valid number of minutes (1-1440).');
        return false;
      }
    }
    return true;
  };

  const handleArm = async () => {
    if (isPending || !roomId || !schedTime) return;
    if (!validateCustomDuration()) return;

    const [h, m] = schedTime.split(':').map((n) => parseInt(n, 10));
    if (isNaN(h) || isNaN(m)) return;

    const nowMs = Date.now() + serverTimeOffset.current;
    const when = new Date(nowMs);
    when.setHours(h, m, 0, 0);
    if (when.getTime() <= nowMs) when.setDate(when.getDate() + 1); // Already passed today → tomorrow

    const prSec = preRoll !== 'Off' ? parseInt(preRoll, 10) : 0;

    setIsPending(true);
    try {
      const { data, error } = await supabase.from('studio_sessions').upsert({
        room_id: roomId,
        status: 'idle',
        timer_mode: timerMode,
        total_duration_seconds: idleDuration,
        pre_roll_duration_seconds: prSec,
        timer_target: null,
        scheduled_start: when.toISOString(),
        live_cue: null
      }).select().single();

      if (error) alert('DATABASE ERROR: ' + error.message);
      else setSessionData(data);
    } finally {
      setIsPending(false);
    }
  };

  const handleCancelSchedule = async () => {
    if (isPending || !roomId) return;
    setIsPending(true);
    try {
      const { data, error } = await supabase.from('studio_sessions')
        .update({ scheduled_start: null }).eq('room_id', roomId).select().single();
      if (error) alert('DATABASE ERROR: ' + error.message);
      else setSessionData(data);
    } finally {
      setIsPending(false);
    }
  };

  /* ── Database State Operations ── */
  const handleStart = async () => {
    if (isPending) return;
    if (!roomId) {
      alert('ERROR: No Room ID selected. Please click a room in the sidebar.');
      return;
    }
    
    // --- INPUT SANITATION SHIELD ---
    if (!validateCustomDuration()) return; // halt execution before touching DB
    
    const prSec = preRoll !== 'Off' ? parseInt(preRoll, 10) : 0;
    
    setIsPending(true);
    try {
      // Deterministic Mathematical Timer Sync
      // If pre-roll is active, add an exact 1000ms UI buffer so the fade transition 
      // doesn't eat the first second of the countdown. 
      const uiTransitionBuffer = prSec > 0 ? 1000 : 0;
      
      const target = new Date(Date.now() + serverTimeOffset.current + (prSec * 1000) + (idleDuration * 1000) + uiTransitionBuffer).toISOString();
      const timerTarget = timerMode === 'up'
        ? new Date(Date.now() + serverTimeOffset.current + (prSec * 1000) + uiTransitionBuffer).toISOString()
        : target;
      
      const { data, error } = await supabase.from('studio_sessions').upsert({
        room_id: roomId,
        status: 'active',
        timer_mode: timerMode,
        total_duration_seconds: idleDuration,
        pre_roll_duration_seconds: prSec,
        timer_target: timerTarget,
        scheduled_start: null,
        live_cue: null
      }).select().single();
      
      if (error) { alert('DATABASE ERROR: ' + error.message); return; }
      setSessionData(data);

      setActiveLiveChip('+5 Min');
      setLiveNote('');
    } catch (err) {
      alert('UNEXPECTED ERROR: ' + err.message);
    } finally {
      setIsPending(false);
    }
  };

  const handlePause = async () => {
    if (isPending) return;
    if (!roomId || !isRunning) return;
    
    setIsPending(true);
    try {
      // Self-healing: if timer_target got lost, fallback to what's currently on screen
      let rem = localTime;
      if (sessionData?.timer_target) {
        rem = Math.max(0, Math.ceil((new Date(sessionData.timer_target).getTime() - (Date.now() + serverTimeOffset.current)) / 1000));
        if (sessionData.timer_mode === 'up') {
          rem = Math.max(0, Math.ceil(((Date.now() + serverTimeOffset.current) - new Date(sessionData.timer_target).getTime()) / 1000));
        }
      }
      
      const { data, error } = await supabase.from('studio_sessions').update({
        status: 'paused',
        paused_time_remaining: rem,
        timer_target: null
      }).eq('room_id', roomId).select().single();
      
      if (error) {
        alert('DATABASE ERROR: ' + error.message);
      } else {
        setLocalPhase(null); // Unconditionally clear lock so UI syncs to DB
        setSessionData(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsPending(false);
    }
  };

  const handleResume = async () => {
    if (isPending) return;
    if (!roomId || !isPaused) return;
    
    setIsPending(true);
    try {
      const pausedRem = sessionData?.paused_time_remaining || localTime || 0;
      // Calculate new target adjusted by server drift (0ms artificial latency)
      const target = new Date(Date.now() + serverTimeOffset.current + (pausedRem * 1000)).toISOString();
      const resumeTarget = sessionData?.timer_mode === 'up'
        ? new Date(Date.now() + serverTimeOffset.current - (pausedRem * 1000)).toISOString()
        : target;
      
      const { data, error } = await supabase.from('studio_sessions').update({
        status: 'active',
        timer_target: resumeTarget,
        paused_time_remaining: 0
      }).eq('room_id', roomId).select().single();
      
      if (error) {
        alert('DATABASE ERROR: ' + error.message);
      } else {
        setLocalPhase(null);
        setSessionData(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsPending(false);
    }
  };

  const resetToIdle = async () => {
    if (!roomId) return;
    
    // Aggressively kill the stale animation frame to prevent it from overwriting the lock
    if (requestRef.current) cancelAnimationFrame(requestRef.current);
    
    // 1. MASTER OVERRIDE: Destroy all locked UI states unconditionally
    transitionLock.current = null;
    lastStatus.current = 'idle';
    setLocalPhase('idle'); // Instantly lock UI to idle (Clears SESSION COMPLETE)
    
    // OPTIMISTIC UPDATE: Stop the tick loop from re-triggering old states while DB responds
    setSessionData(prev => prev ? {
      ...prev,
      status: 'idle',
      timer_target: null,
      paused_time_remaining: 0,
      live_cue: null
    } : null);

    // DO NOT manually setClockMode here, let the robust useEffect fader handle the transition safely
    setLocalTime(idleDuration); // Force local timer back to default preset
    setLocalPreRoll(0);
    setCustomAdd('');
    setActiveLiveChip('+5 Min');
    setLiveNote('');
    
    // 2. Broadcast clean update to Supabase, overriding frozen backend rows
    setIsPending(true);
    try {
      const { data, error } = await supabase.from('studio_sessions').update({
        status: 'idle',
        timer_target: null,
        paused_time_remaining: 0,
        live_cue: null
      }).eq('room_id', roomId).select().single();
      
      if (error) alert('DATABASE ERROR: ' + error.message);
      else setSessionData(data);
      
    } finally {
      setIsPending(false);
    }
  };

  const handleEndConfirmed = () => {
    setShowEndModal(false);
    resetToIdle();
    if (cueTimeoutRef.current) clearTimeout(cueTimeoutRef.current);
  };

  const handleLiveChip = async (chip) => {
    setActiveLiveChip(chip);
    const addMap = { '+1 Min': 1, '+5 Min': 5, '+10 Min': 10 };
    if (addMap[chip] && roomId) {
      await adjustTime(addMap[chip]);
    } else {
      setTimeout(() => customAddRef.current?.focus(), 50);
    }
  };

  /* ── UI Helpers ── */
  const displayPath = `/display/${roomId || ''}`;
  const displayUrl  = `${window.location.origin}/#${displayPath}`;

  const handleCopyLink = () => {
    if (!roomId) return;
    navigator.clipboard?.writeText(displayUrl).catch(() => {});
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 1800);
  };

  const handleShareSubmit = async (e) => {
    e.preventDefault();
    if (!shareEmail.trim() || !roomId || isSharing) return;
    
    setIsSharing(true);
    try {
      const { error } = await supabase.from('invitations').insert({
        room_id: roomId,
        guest_email: shareEmail.trim()
      });
      
      if (error) throw error;
      
      setShareFeedback('Invite Sent!');
      setTimeout(() => {
        setIsShareModalOpen(false);
        setShareEmail('');
        setShareFeedback('');
      }, 1500);
      
    } catch (err) {
      console.error(err);
      alert('Error sending invite: ' + err.message);
    } finally {
      setIsSharing(false);
    }
  };

  const statusLabel = {
    'idle':     'IDLE',
    'preroll':  'PRE-ROLL',
    'active':   'RUNNING',
    'paused':   'PAUSED',
    'complete': 'COMPLETE',
  }[status] || 'IDLE';

  const renderTime = isIdle ? idleDuration : localTime;
  const displayedTime = isIdle && timerMode === 'up' ? 0 : renderTime;
  const minutes = Math.floor(displayedTime / 60);
  const seconds = displayedTime % 60;
  const formattedTime = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  const fracState = isIdle 
    ? { color: '#ffffff', pulse: false, key: 'default' }
    : getFractionalState(localTime, sessionData?.total_duration_seconds, sessionData?.timer_mode);

  const armedLabel = scheduledStart
    ? new Date(scheduledStart).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })
    : '';
  const effectiveStartMode = isArmed ? 'Auto-Start' : startMode;

  // Inline JSX node replaces inline function component to prevent React unmount/remount click dropping
  const endSessionBtnNode = (
    <button
      onClick={() => setShowEndModal(true)}
      disabled={isPending}
      className={`w-full sm:w-auto sm:flex-1 min-h-[56px] flex items-center justify-center gap-2 font-bold text-sm py-4 rounded-xl tracking-[0.15em] transition-all duration-200 active:scale-[0.99] ${isPending ? 'bg-[#151515] text-red-500/40 border border-red-900/20 cursor-not-allowed' : 'bg-[#151515] text-red-500 border border-red-900/50 hover:border-red-700/60 hover:text-red-400'}`}
    >
      ■ END SESSION
    </button>
  );

  const sectionClass = "mb-10 md:mb-12";

  return (
    <main className="flex-1 h-full flex flex-col overflow-y-auto bg-obsidian pt-[128px] md:pt-0">
      <div className="flex-1 flex flex-col px-5 py-8 md:px-12 md:py-12 w-full max-w-4xl mx-auto">

        {/* ── CLOCK SECTION ── */}
        <section className={sectionClass}>
          <div className="flex flex-wrap sm:flex-row items-center justify-between gap-x-4 gap-y-3 mb-5 md:mb-6">
            <p className="text-[0.65rem] font-semibold text-muted tracking-[0.22em] uppercase transition-all duration-300">
              {roomName.toUpperCase()} — {statusLabel}{isArmed && isIdle ? ` · ARMED ${armedLabel.toUpperCase()}` : ''}
            </p>
            <div className={`flex items-center gap-1 transition-opacity duration-500 ${isComplete ? 'opacity-20 pointer-events-none' : ''}`}>
              <button disabled={!roomId} onClick={() => setIsShareModalOpen(true)} className="display-action-btn flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-150 disabled:opacity-50">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
                <span className="text-[0.65rem] font-semibold tracking-[0.08em] uppercase">Share Room</span>
              </button>
              <span className="w-px h-3.5 bg-[#282828] mx-0.5" />
              <button disabled={!roomId} onClick={handleCopyLink} className="display-action-btn flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-150 disabled:opacity-50">
                {copyFeedback 
                  ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 7l3.5 3.5L12 3.5"/></svg>
                  : <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="5" width="8" height="8" rx="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2"/></svg>
                }
                <span className="text-[0.65rem] font-semibold tracking-[0.08em] uppercase">
                  {copyFeedback ? 'Copied' : 'Copy Link'}
                </span>
              </button>
              <span className="w-px h-3.5 bg-[#282828] mx-0.5" />
              <a href={roomId ? `/#${displayPath}` : '#'} target="_blank" rel="noopener noreferrer" className={`display-action-btn flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-150 ${!roomId ? 'opacity-50 pointer-events-none' : ''}`}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2H2.5A1.5 1.5 0 001 3.5v8A1.5 1.5 0 002.5 13h8A1.5 1.5 0 0012 11.5V8"/><path d="M8.5 1H13v4.5"/><path d="M13 1L6.5 7.5"/></svg>
                <span className="text-[0.65rem] font-semibold tracking-[0.08em] uppercase">Open Display</span>
              </a>
            </div>
          </div>

          <div className={`relative flex items-center min-h-[160px] md:min-h-[200px] transition-opacity duration-300 ${clockVisible ? 'opacity-100' : 'opacity-0'}`}>
            {showCompleteClock ? (
              <div className="flex flex-col items-center justify-center w-full text-center py-4">
                <div className="mb-4 rounded-full bg-red-600/80" style={{ width: '4rem', height: '2px' }} />
                <span className="uppercase block text-center text-red-600" style={{ fontFamily: "'Inter', sans-serif", fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, letterSpacing: '0.2em', paddingLeft: '0.2em', lineHeight: 1.1 }}>
                  SESSION<br />COMPLETE
                </span>
                <div className="mt-4 rounded-full bg-red-600/80" style={{ width: '4rem', height: '2px' }} />
              </div>
            ) : (
              <>
                <div className="relative flex flex-col justify-center">
                  {showPreRollClock ? (
                    <div className="flex flex-col leading-none pt-2">
                      <span className="uppercase tracking-[0.3em] leading-none" style={{ color: '#FAFAFA', fontSize: 'clamp(0.75rem, 2.5vw, 1.25rem)', marginBottom: '0.8rem', fontWeight: 600 }}>WE ARE LIVE IN</span>
                      <span className="font-bold leading-none tabular-nums" style={{ fontFamily: "'Roboto Mono', 'JetBrains Mono', monospace", fontSize: 'clamp(3.5rem, 14vw, 10.5rem)', letterSpacing: '-0.02em', color: '#D4AF37' }}>{localPreRoll}</span>
                    </div>
                  ) : (
                    <span 
                      key={fracState.key}
                      className={`timer-display select-none tabular-nums transition-opacity duration-300 ${showPausedClock ? 'opacity-30' : 'opacity-100'} ${fracState.pulse ? 'timer-pulse' : ''}`} 
                      style={{ 
                        fontSize: 'clamp(3.5rem, 14vw, 10.5rem)',
                        color: fracState.color,
                        transition: 'color 700ms ease-in-out, opacity 300ms ease-in-out'
                      }}
                    >
                      {formattedTime}
                    </span>
                  )}

                  {/* ── LIVE CUE MIRROR ── */}
                  <div 
                    className="absolute left-1/2 -translate-x-1/2 flex justify-center pointer-events-none z-10" 
                    style={{ 
                      width: 'max-content',
                      maxWidth: 'min(100vw - 40px, 600px)',
                      top: '100%',
                      marginTop: '0.25rem',
                      opacity: (sessionData?.live_cue && (isRunning || isPaused)) ? 1 : 0, 
                      transition: 'opacity 700ms ease-in-out' 
                    }}
                  >
                    <span 
                      className="uppercase text-white text-center break-words whitespace-normal leading-snug w-full" 
                      style={{ 
                        fontFamily: "'Inter', sans-serif", 
                        fontSize: 'clamp(0.85rem, 2.5vw, 1.2rem)', 
                        fontWeight: 600, 
                        letterSpacing: '0.25em' 
                      }}
                    >
                      {sessionData?.live_cue}
                    </span>
                  </div>
                </div>

                <div className={`transition-opacity duration-300 ${showPreRollClock ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
                  <TimerAdjuster onIncrement={() => adjustTime(5)} onDecrement={() => adjustTime(-5)} />
                </div>
              </>
            )}
          </div>
        </section>

        {/* ── DURATION PRESETS / ADD TIME ── */}
        <section className={`${sectionClass} transition-opacity duration-500 ${isComplete ? 'opacity-20 pointer-events-none' : ''}`}>
          <p className="text-[0.65rem] font-semibold text-muted tracking-[0.22em] uppercase mb-4 transition-all duration-200">
            {isLive ? 'ADD TIME' : 'DURATION PRESETS'}
          </p>
          {isIdle && (
            <div className="flex flex-wrap items-center gap-2">
              {IDLE_PRESETS.map((preset) => (
                <button key={preset} onClick={() => handlePresetClick(preset)} className={`min-h-[44px] px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all duration-150 border ${activePreset === preset ? 'bg-white text-black border-white' : 'bg-[#0f0f0f] text-muted border-[#222222] hover:text-white hover:border-[#3a3a3a]'}`}>
                  {preset === 'Custom' ? 'Custom Setup' : preset === 120 ? '2 Hours' : `${preset} Min`}
                </button>
              ))}
              {activePreset === 'Custom' && (
                <div className="flex items-center border border-[#2e2e2e] rounded-full overflow-hidden bg-[#0f0f0f] min-h-[44px]">
                  <input ref={customInputRef} type="number" min="1" max="1440" value={customMin} onChange={(e) => setCustomMin(e.target.value)} placeholder="—" className="custom-min-input w-12 bg-transparent text-white text-xs font-semibold pl-4 pr-1 py-2 focus:outline-none placeholder-[#333333]" />
                  <span className="text-[#444444] text-xs pr-4 font-medium">min</span>
                </div>
              )}
            </div>
          )}
          {isLive && (
            <div className="flex flex-wrap items-center gap-2">
              {LIVE_CHIPS.map((chip) => (
                <button key={chip} onClick={() => handleLiveChip(chip)} className={`min-h-[44px] px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all duration-150 border ${activeLiveChip === chip ? 'bg-white text-black border-white' : 'bg-[#0f0f0f] text-muted border-[#222222] hover:text-white hover:border-[#3a3a3a]'}`}>
                  {chip}
                </button>
              ))}
              {activeLiveChip === '+ Custom' && (
                <div className="flex items-center border border-[#2e2e2e] rounded-full overflow-hidden bg-[#0f0f0f] min-h-[44px]">
                  <input 
                    ref={customAddRef} 
                    type="number" 
                    min="1" 
                    max="1440" 
                    value={customAdd} 
                    onChange={(e) => setCustomAdd(e.target.value)} 
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCustomAddSubmit(); }}
                    placeholder="—" 
                    className="custom-min-input w-12 bg-transparent text-white text-xs font-semibold pl-4 pr-1 py-2 focus:outline-none placeholder-[#333333]" 
                  />
                  <span className="text-[#444444] text-xs pr-3 font-medium">min</span>
                  <button 
                    onClick={handleCustomAddSubmit} 
                    disabled={isPending}
                    className="text-[0.65rem] font-bold tracking-wider px-3 h-full py-3 border-l border-[#2e2e2e] text-white hover:bg-[#1a1a1a] transition-colors disabled:opacity-50"
                  >
                    ADD
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── SESSION SETTINGS ── */}
        <section className={`${sectionClass} transition-opacity duration-500 ${isComplete ? 'opacity-20 pointer-events-none' : ''}`}>
          <p className="text-[0.65rem] font-semibold text-muted tracking-[0.22em] uppercase mb-4">Session Settings</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full">
            <span className="text-sm text-muted font-medium shrink-0">Pre-Roll</span>
            <div className="w-full sm:flex-1 sm:min-w-0">
              <SegmentedControl options={PRE_ROLL_OPTIONS} active={preRoll} onChange={setPreRoll} />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full mt-4">
            <span className="text-sm text-muted font-medium shrink-0">Timer</span>
            <div className="w-full sm:flex-1 sm:min-w-0">
              <SegmentedControl
                options={TIMER_MODE_OPTIONS}
                active={timerMode === 'up' ? 'Count Up' : 'Count Down'}
                onChange={(value) => {
                  if (isIdle) setTimerMode(value === 'Count Up' ? 'up' : 'down');
                }}
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full mt-4">
            <span className="text-sm text-muted font-medium shrink-0">Start</span>
            <div className="w-full sm:flex-1 sm:min-w-0">
              <SegmentedControl
                options={START_MODE_OPTIONS}
                active={effectiveStartMode}
                onChange={(value) => {
                  if (!isIdle || isArmed) return;
                  setStartMode(value);
                }}
              />
            </div>
          </div>
          {isIdle && effectiveStartMode === 'Auto-Start' && (
            <div className="flex flex-wrap items-center gap-3 mt-4">
              {isArmed ? (
                <>
                  <span className="text-sm text-white font-medium">Armed for {armedLabel}</span>
                  <button
                    onClick={handleCancelSchedule}
                    disabled={isPending}
                    className="min-h-[44px] px-5 rounded-full text-xs font-bold tracking-[0.12em] border border-red-900/50 text-red-500 bg-[#151515] hover:border-red-700/60 hover:text-red-400 transition-colors disabled:opacity-50"
                  >
                    CANCEL
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="time"
                    value={schedTime}
                    onChange={(e) => setSchedTime(e.target.value)}
                    className="min-h-[44px] bg-[#0f0f0f] border border-[#222222] rounded-full px-4 text-sm font-semibold text-white focus:outline-none focus:border-[#3a3a3a] [color-scheme:dark]"
                  />
                  <button
                    onClick={handleArm}
                    disabled={isPending || !schedTime}
                    className="min-h-[44px] px-5 rounded-full text-xs font-bold tracking-[0.12em] bg-white text-black hover:bg-[#e8e8e8] transition-colors disabled:opacity-50"
                  >
                    ARM
                  </button>
                  <span className="text-[0.65rem] text-[#555555] tracking-wide">Keep this dashboard open. Starts at this local time.</span>
                </>
              )}
            </div>
          )}
        </section>

        {/* ── LIVE CUES (RESTRICTED TO RUNNING OR PAUSED) ── */}
        <section className={`mb-auto transition-opacity duration-500 ${(!isRunning && !isPaused) ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
          <div className="flex flex-wrap items-center justify-between gap-y-3 gap-x-4 mb-4">
            <div className="flex items-center gap-3">
              <p className="text-[0.65rem] font-semibold text-muted tracking-[0.22em] uppercase">Live Cues</p>
              {sessionData?.live_cue && (
                <span className="px-2 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] text-[0.6rem] font-bold tracking-[0.1em] uppercase">
                  Active
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              {sessionData?.live_cue && (
                <button 
                  onClick={handleClearCue} 
                  className="text-[0.65rem] font-bold text-red-500 hover:text-red-400 tracking-[0.1em] transition-colors flex items-center gap-1"
                >
                  CLEAR CUE ✕
                </button>
              )}
              <span className="text-[0.65rem] font-medium text-[#555555] tracking-widest">{liveNote.length}/40</span>
            </div>
          </div>
          <div className="relative w-full flex items-center">
            <input 
              type="text" 
              maxLength={40} 
              value={liveNote} 
              onChange={(e) => setLiveNote(e.target.value)} 
              onKeyDown={(e) => { if (e.key === 'Enter') handleSendCue(); }} 
              placeholder="Push a live note to the display..." 
              className="w-full bg-[#0f0f0f] border border-[#222222] rounded-xl py-4 pl-4 md:pl-5 pr-20 md:pr-24 text-sm md:text-base placeholder-[#383838] focus:outline-none focus:border-[#3a3a3a] transition-colors" 
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {liveNote && (
                <button 
                  onClick={() => setLiveNote('')} 
                  title="Clear Input" 
                  className="w-9 h-9 flex items-center justify-center text-muted hover:text-white transition-colors rounded-lg hover:bg-[#1e1e1e]"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 2.5l9 9m0-9l-9 9" /></svg>
                </button>
              )}
              <button 
                onClick={handleSendCue} 
                disabled={!liveNote.trim()} 
                title="Push Cue" 
                className="w-9 h-9 flex items-center justify-center text-muted hover:text-white transition-colors rounded-lg hover:bg-[#1e1e1e] disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-muted cursor-pointer"
              >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13.5 7.5L1.5 1.5l3 6-3 6 12-6z"/></svg>
              </button>
            </div>
          </div>
        </section>

        {/* ── ACTION BUTTONS ── */}
        <section className="mt-10 md:mt-12">
          {isComplete ? (
            <button onClick={handleEndConfirmed} disabled={isPending} className={`w-full min-h-[64px] flex items-center justify-center font-extrabold text-sm md:text-base py-5 rounded-xl tracking-[0.2em] transition-all duration-300 active:scale-[0.99] ${isPending ? 'bg-[#e8e8e8] text-black/50 cursor-not-allowed' : 'bg-white text-black hover:bg-[#e8e8e8]'}`}>
              RESET ROOM
            </button>
          ) : isIdle ? (
            <button onClick={handleStart} disabled={isPending} className={`w-full min-h-[56px] flex items-center justify-center font-bold text-sm md:text-base py-4 md:py-5 rounded-xl tracking-[0.15em] transition-all duration-200 active:scale-[0.99] ${isPending ? 'bg-[#e8e8e8] text-black/50 cursor-not-allowed' : 'bg-white text-black hover:bg-[#e8e8e8]'}`}>
              START SESSION
            </button>
          ) : (isRunning || isPreRoll) ? (
            <div className="flex flex-col sm:flex-row items-stretch gap-3 md:gap-4">
              <button onClick={isPreRoll ? undefined : handlePause} disabled={isPreRoll || isPending} className={`w-full sm:w-auto sm:flex-1 min-h-[56px] flex items-center justify-center gap-2 font-bold text-sm py-4 rounded-xl tracking-[0.15em] border transition-all duration-200 active:scale-[0.99] ${(isPreRoll || isPending) ? 'bg-[#151515] text-white/40 border-[#333333]/50 cursor-not-allowed' : 'bg-[#151515] text-white border-[#333333] hover:border-[#555555]'}`}>
                ⏸ PAUSE SESSION
              </button>
              {endSessionBtnNode}
            </div>
          ) : isPaused ? (
            <div className="flex flex-col sm:flex-row items-stretch gap-3 md:gap-4">
              <button onClick={handleResume} disabled={isPending} className={`w-full sm:w-auto sm:flex-1 min-h-[56px] flex items-center justify-center gap-2 font-bold text-sm py-4 rounded-xl tracking-[0.15em] transition-all duration-200 active:scale-[0.99] ${isPending ? 'bg-[#e8e8e8] text-black/50 cursor-not-allowed' : 'bg-white text-black hover:bg-[#e8e8e8]'}`}>
                ▶ RESUME SESSION
              </button>
              {endSessionBtnNode}
            </div>
          ) : null}
        </section>
      </div>

      {showEndModal && <EndSessionModal room={roomName} onConfirm={handleEndConfirmed} onCancel={() => setShowEndModal(false)} />}
      
      {isShareModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-[#0a0a0a] border border-[#222222] rounded-2xl p-6 shadow-2xl relative">
            <button 
              onClick={() => { setIsShareModalOpen(false); setShareEmail(''); setShareFeedback(''); }}
              className="absolute top-4 right-4 text-[#555555] hover:text-white transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="12" x2="18" y2="12"></line></svg>
            </button>
            <h2 className="text-lg font-bold text-white mb-2">Share Room</h2>
            <p className="text-xs text-[#888888] mb-6">Invite a guest to view this room by entering their email address.</p>
            
            <form onSubmit={handleShareSubmit} className="space-y-4">
              <div>
                <input 
                  type="email" 
                  required 
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="Guest Email Address" 
                  className="w-full bg-[#111111] border border-[#222222] rounded-xl px-4 py-3 text-sm font-medium text-white placeholder-[#555555] focus:outline-none focus:border-[#444444] transition-colors"
                  autoFocus
                />
              </div>
              
              <button 
                type="submit" 
                disabled={isSharing || shareFeedback !== ''}
                className={`w-full min-h-[48px] flex items-center justify-center font-bold text-sm py-3 rounded-xl tracking-wide transition-all duration-200 active:scale-[0.99] ${isSharing || shareFeedback !== '' ? 'bg-[#e8e8e8] text-black/50 cursor-not-allowed' : 'bg-white text-black hover:bg-[#e8e8e8]'}`}
              >
                {shareFeedback || (isSharing ? 'Sending...' : 'Send Invite')}
              </button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}